import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { publicApi } from "../../api";
import SEO, { SEOPresets } from "../../components/SEO";
import {
  Star,
  Check,
  X,
  ExternalLink,
  ArrowRight,
  Award,
  Zap,
  DollarSign,
} from "lucide-react";

const NicheComparison = () => {
  const { slug } = useParams();
  const [niche, setNiche] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [nicheRes, productsRes] = await Promise.all([
          publicApi.getNicheBySlug(slug),
          publicApi.getProductsByNiche(slug),
        ]);
        setNiche(nicheRes.data);
        setProducts(productsRes.data);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const handleAffiliateClick = async (product, source) => {
    try {
      await publicApi.trackClick({
        product_id: product.id,
        niche_id: product.niche_id,
        source_page: source,
      });
    } catch (e) {
      console.error("Failed to track click");
    }
    window.open(product.affiliate_link, "_blank");
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
      );
    }
    if (hasHalf) {
      stars.push(
        <Star key="half" size={16} className="text-yellow-400 fill-yellow-400/50" />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="comparison-loading">
        <div className="animate-pulse text-slate-500">Loading comparison...</div>
      </div>
    );
  }

  if (!niche) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Category not found</h1>
        <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">
          Go back home
        </Link>
      </div>
    );
  }

  // Determine badges
  const bestOverall = products[0];
  const bestValue = products.find((p) => p.has_free_tier) || products[1];
  const topRated = [...products].sort((a, b) => b.rating - a.rating)[0];

  return (
    <div data-testid="niche-comparison-page">
      <SEO
        title={`Best ${niche.name} ${new Date().getFullYear()} - Reviews & Comparison`}
        description={niche.meta_description || `Compare the best ${niche.name.toLowerCase()}: ${products.slice(0, 5).map(p => p.name).join(', ')}. Side-by-side features, pricing, and honest reviews.`}
        url={`/${niche.slug}`}
        keywords={`${niche.name}, best ${niche.name.toLowerCase()}, ${niche.name.toLowerCase()} comparison, ${products.map(p => p.name).join(', ')}`}
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-wider">
              {niche.category}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-white mt-2 font-serif">
              {niche.name}
            </h1>
            <p className="text-lg text-slate-300 mt-3 leading-relaxed">
              {niche.description}
            </p>
            <p className="text-slate-400 text-sm mt-4">
              Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </p>
          </div>
        </div>
      </section>

      {/* Quick Comparison Table */}
      {products.length > 0 && (
        <section className="py-10 bg-white border-b border-slate-200">
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-bold text-slate-900 font-serif mb-6">
              Quick Comparison
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]" data-testid="comparison-table">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left py-4 px-4 font-semibold text-slate-700">Tool</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-700">Best For</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-700">Rating</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-700">Starting Price</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-700">Free Tier</th>
                    <th className="text-center py-4 px-4 font-semibold text-slate-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map((product, index) => (
                    <tr
                      key={product.id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`comparison-row-${product.slug}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          {product.logo_url ? (
                            <img
                              src={product.logo_url}
                              alt={product.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                              {product.name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">
                                {product.name}
                              </span>
                              {product === bestOverall && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Award size={10} />
                                  Best Overall
                                </span>
                              )}
                              {product === bestValue && product !== bestOverall && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <DollarSign size={10} />
                                  Best Value
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-slate-500">
                              {product.tagline?.substring(0, 40)}...
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-slate-600">{product.best_for}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1">
                          {renderStars(product.rating)}
                          <span className="ml-1 text-slate-600 font-medium">
                            {product.rating.toFixed(1)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 font-semibold text-slate-900">
                        {product.starting_price || "Contact"}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {product.has_free_tier ? (
                          <Check className="text-green-500 mx-auto" size={20} />
                        ) : (
                          <X className="text-slate-300 mx-auto" size={20} />
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <button
                          onClick={() => handleAffiliateClick(product, "comparison")}
                          className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
                          data-testid={`visit-${product.slug}-btn`}
                        >
                          Visit Site
                          <ExternalLink size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Detailed Product Cards */}
      <section className="py-12 bg-slate-50">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl font-bold text-slate-900 font-serif mb-6">
            Detailed Reviews
          </h2>
          <div className="space-y-6">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                data-testid={`product-card-${product.slug}`}
              >
                <div className="p-5 md:p-6">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      {product.logo_url ? (
                        <img
                          src={product.logo_url}
                          alt={product.name}
                          className="w-14 h-14 rounded-xl object-contain bg-white border border-slate-100"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-bold">
                          {product.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-2xl font-bold text-slate-900 font-serif">
                            {product.name}
                          </h3>
                          {index === 0 && (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                              #1 Pick
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 mt-1">{product.tagline}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex items-center">
                            {renderStars(product.rating)}
                          </div>
                          <span className="font-semibold text-slate-900">
                            {product.rating.toFixed(1)}/5
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <span className="text-sm text-slate-500">Starting at</span>
                        <p className="text-2xl font-bold text-slate-900">
                          {product.starting_price || "Contact"}
                        </p>
                      </div>
                      <button
                        onClick={() => handleAffiliateClick(product, "review")}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                        data-testid={`cta-${product.slug}-btn`}
                      >
                        Try {product.name}
                        <ExternalLink size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-slate-600 mt-6 leading-relaxed">
                    {product.description}
                  </p>

                  {/* AI Verdict */}
                  {product.ai_verdict && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2 text-blue-700 font-semibold mb-2">
                        <Zap size={16} />
                        Quick Verdict
                      </div>
                      <p className="text-slate-700">{product.ai_verdict}</p>
                    </div>
                  )}

                  {/* Pros & Cons */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    {product.pros?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-green-700 mb-3">Pros</h4>
                        <ul className="space-y-2">
                          {product.pros.slice(0, 4).map((pro, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-600">
                              <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                              <span>{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {product.cons?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-red-700 mb-3">Cons</h4>
                        <ul className="space-y-2">
                          {product.cons.slice(0, 4).map((con, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-600">
                              <X size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                              <span>{con}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Key Features */}
                  {(product.primary_metric || product.unique_feature_1) && (
                    <div className="mt-6 pt-6 border-t border-slate-100">
                      <h4 className="font-semibold text-slate-900 mb-3">Key Features</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {product.primary_metric && (
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-xs text-slate-500 uppercase tracking-wider">
                              {product.primary_metric}
                            </span>
                            <p className="font-semibold text-slate-900 mt-1">
                              {product.primary_metric_value}
                            </p>
                          </div>
                        )}
                        {product.unique_feature_1 && (
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-xs text-slate-500 uppercase tracking-wider">
                              {product.unique_feature_1}
                            </span>
                            <p className="font-semibold text-slate-900 mt-1">
                              {product.unique_feature_1_value}
                            </p>
                          </div>
                        )}
                        {product.unique_feature_2 && (
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-xs text-slate-500 uppercase tracking-wider">
                              {product.unique_feature_2}
                            </span>
                            <p className="font-semibold text-slate-900 mt-1">
                              {product.unique_feature_2_value}
                            </p>
                          </div>
                        )}
                        {product.has_free_tier && (
                          <div className="p-3 bg-green-50 rounded-lg">
                            <span className="text-xs text-green-600 uppercase tracking-wider">
                              Free Tier
                            </span>
                            <p className="font-semibold text-green-700 mt-1">
                              Available
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {products.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <p className="text-slate-500 mb-4">No products in this category yet.</p>
              <Link
                to="/admin/products"
                className="text-blue-600 hover:underline"
              >
                Add products in the admin dashboard
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NicheComparison;
